import { OK } from 'http-status-codes';
import {
  getPeriod,
  getFormattedPeriod,
  getLastMonthCompetence,
  getFirstAndLastDaysByCompetence,
  getFormattedPeriodFromResponse,
} from 'packages/common/utils/format.js';
import resource from '../../resource';
import { SERVICE_TAKER_ALLOCATION_FIELDS_RULES } from './constants';
import {
  clickSaveRegistrationServiceTakerAllocationTracker,
  clickSaveEditionServiceTakerAllocationTracker,
} from './serviceTakerAllocationTrackerService';

const FIELD_TYPES = ['allocationDays', 'allocationHours', 'allocationPercentage'];

export function checkHasAllocationForTenant() {
  return resource.checkHasAllocationForTenant()
    .then(response => response.status === OK);
}

export function getServiceTakersAllocationsList(period, { pagination } = {}) {
  const competence = getCompetenceFromPeriod(period);
  const page = pagination?.page ? pagination.page - 1 : 0;
  const size = pagination?.pageSize ? pagination.pageSize : 10;

  return getServiceTakersAllocations({
    page, transformType: 'LIST_ITEM', size, competence,
  });
}

export function getServiceTakersAllocations(params = {}) {
  return resource.getServiceTakersAllocations(params)
    .then(parseEmployeeContent);
}

export function getServiceTakerAllocation(id) {
  return resource.getServiceTakerAllocation(id)
    .then(parseAllocationContent);
}

function parseTrackProperties(data) {
  const {
    serviceTaker: { id },
    competence: period,
    allocations,
  } = data;
  const competence = periodToCompetence(period);

  return {
    serviceTaker: id,
    allocations: allocations?.length,
    competence,
  };
}

export async function createServiceTakerAllocations(data) {
  const competence = periodToCompetence(data.competence);
  await validateAllocations(competence, data.allocationType, data.allocations);
  data.allocations = clearAndSetAllocationsValues(
    data.allocations,
    SERVICE_TAKER_ALLOCATION_FIELDS_RULES[data.allocationType.value]
  );

  const properties = parseTrackProperties(data);
  clickSaveRegistrationServiceTakerAllocationTracker(properties);

  return resource.createServiceTakerAllocations({ ...data, competence });
}

export async function updateServiceTakerAllocations(data) {
  const competence = periodToCompetence(data.competence);
  await validateAllocations(competence, data.allocationType, data.allocations);
  data.allocations = clearAndSetAllocationsValues(
    data.allocations,
    SERVICE_TAKER_ALLOCATION_FIELDS_RULES[data.allocationType.value]
  );

  const properties = parseTrackProperties(data);
  clickSaveEditionServiceTakerAllocationTracker(properties);

  return resource.updateServiceTakerAllocations({ ...data, competence });
}

export async function validateAllocations(competence, allocationType, allocations) {
  const dayLimits = getFirstAndLastDaysByCompetence(competence);
  const dayLimitsInNumber = { start: Number(dayLimits.start), end: Number(dayLimits.end) };
  const hourLimits = { start: 1, end: 227 };
  const percentLimits = { start: 0, end: 100 };

  allocations.forEach(allocation => {
    const value = allocation.allocationValue;

    switch (allocationType.name) {
    case 'DAY':
      validateAllocation(dayLimitsInNumber, value, SERVICE_TAKER_ALLOCATION_FIELDS_RULES.DAY);
      break;
    case 'HOUR':
      validateAllocation(
        hourLimits,
        value?.split(':')?.[0],
        SERVICE_TAKER_ALLOCATION_FIELDS_RULES.HOUR
      );
      break;
    case 'PERCENTAGE':
      validateAllocation(
        percentLimits,
        value,
        SERVICE_TAKER_ALLOCATION_FIELDS_RULES.PERCENTAGE
      );
      break;
    default: break;
    }
  });
}

function clearAndSetAllocationsValues(allocations, allocationType) {
  return allocations.map(allocation => {
    allocation.allocationHours = null;
    allocation.allocationDays = null;
    allocation.allocationPercentage = null;

    allocation[allocationType.correspondingField] = allocation.allocationValue;
    return allocation;
  });
}

export function validateAllocation(limits, value, allocationType) {
  if (!validateMinMax(limits, value)) {
    throwInvalidAllocationError(allocationType.instance.description);
  }
}

export function throwInvalidAllocationError(field) {
  const errorMessage = { title: 'Erro', content: `Existem valores inválidos para o campo ${field}` };
  throw errorMessage;
}

export function periodToCompetence(period) {
  return getFormattedPeriod(period, 'YYYY-MM-DD');
}

export function getPeriodFormatted(date) {
  return getPeriod(date);
}

export function getLastCompetence() {
  return getLastMonthCompetence();
}

export function getFirstAndLastDaysOfMonth(competence) {
  return getFirstAndLastDaysByCompetence(competence);
}

export function validateMinMax(limits, value) {
  return value >= limits.start && value <= limits.end;
}

export function clearAllocationValues(allocations) {
  return allocations.map(allocation => ({ ...allocation, allocationValue: 0 }));
}

export function buildComponentRules(functionDay, functionHour, functionPercent) {
  const rules = SERVICE_TAKER_ALLOCATION_FIELDS_RULES;
  rules.DAY.componentOptions.customValidations[0].valid = functionDay;
  rules.HOUR.componentOptions.customValidations[0].valid = functionHour;
  rules.PERCENTAGE.componentOptions.customValidations[0].valid = functionPercent;

  return rules;
}

export function reloadComponentRules(allocationTypes, allocationType, limits) {
  allocationTypes[allocationType].limits = limits;
}

function getCompetenceFromPeriod(period) {
  return period
    ? getFormattedPeriod(period.startDate, 'YYYY-MM-DD')
    : getCompetenceFromPeriod(getPeriod());
}

function parseEmployeeContent({ data }) {
  const content = data.content.map(serviceTaker => {
    const allocated = getEmployeesFormattedInformation(serviceTaker.employeesAllocated);
    return { ...serviceTaker, allocated };
  });
  return { ...data, content };
}

function parseAllocationContent({ data }) {
  const allocations = data.allocations.map(allocation => {
    FIELD_TYPES.forEach(field => {
      if (allocation[field]) {
        allocation.allocationValue = parseAllocationValue(
          allocation[field],
          data.allocationType.name
        );
      }
    });

    return allocation;
  });

  return {
    ...data,
    allocations,
    competence: getFormattedPeriodFromResponse(data.competence, 'YYYY-MM-DD'),
  };
}

function parseAllocationValue(allocationValue, allocationType) {
  if (allocationType === 'HOUR') {
    return allocationValue.padStart(6, '0');
  }

  return allocationValue;
}

function getEmployeesFormattedInformation(employees) {
  const firstEmployee = employees[0].employeeName;
  const employeeCount = employees.length;
  const infoComplement = getEmployeeComplement(employeeCount - 1);

  return `${firstEmployee}${infoComplement}`;
}

function getEmployeeComplement(employeeCount) {
  if (employeeCount === 1) return ` + ${employeeCount} pessoa`;
  if (employeeCount > 1) return ` + ${employeeCount} pessoas`;
  return '';
}

export function deleteBatchServiceTakerAllocations(period, data) {
  const allocationIds = parseServiceTakerAllocationDeletePayload(data);

  return resource.deleteBatchServiceTakerAllocations({ serviceTakerAllocationIds: allocationIds });
}

function parseServiceTakerAllocationDeletePayload(data) {
  return data.map(({ id }) => id);
}
